import { api } from 'api/api';
import { EMobileStatus, EFollowUpStatus, IAppointmentCombinedState, ICreateFollowUpRequest, IFormValue, IListValue } from 'models';
import { put, select, takeLatest } from 'redux-saga/effects';
import { messagingService } from 'services';
import { handleSagaError } from 'store/common';
import { actionCreator } from './slice';
import { dateUtil } from 'coreutil';
import { selectors } from 'store/followups/create';
import { selectors as authSelectors } from 'store/auth';
import { IFollowUpStatusResponse } from 'api/followups/controller';
import { PayloadAction } from '@reduxjs/toolkit';

export function* saga(): any {
	yield takeLatest(actionCreator.INIT_START, onInitStart);
	yield takeLatest(actionCreator.CREATE_START, onCreateStart);
}

function* onInitStart(action: ReturnType<typeof actionCreator.INIT_START>): any {
	try {
		const activityId: string = action.payload.activityId;
		const followUps: IAppointmentCombinedState[] = yield api.appointments.getFollowUps(activityId);
		const timeWindows: IListValue[] = yield api.lov.getByValueType('TimeWindows');
		const followUpStatusResponse: IFollowUpStatusResponse = yield api.followUps.getFollowUpStatus(activityId);
		const inPersonStartTimes: IListValue[] = [];
		const phoneStartTimes: IListValue[] = [];
		const durations: string[] = [];
		timeWindows.forEach(time => {
			inPersonStartTimes.push({
				...time,
				value: dateUtil.to24HourTime(time.value)
			});
		});
		timeWindows.forEach((time, index) => {
			const { hour, minute } = dateUtil.parseHourMinutes(time.value);
			phoneStartTimes.push({
				...time,
				value: dateUtil.formatTime24Hour(hour, minute)
			});
			const isLast = index === timeWindows.length - 1;
			if (isLast) return;
			for (let i = 1; i < 4; i++) {
				const totalMinutes = minute + 15 * i;
				const adjustedHour = hour + Math.floor(totalMinutes / 60);
				const adjustedMinute = totalMinutes % 60;
				phoneStartTimes.push({
					...time,
					displayText: dateUtil.formatTime12Hour(adjustedHour, adjustedMinute),
					value: dateUtil.formatTime24Hour(adjustedHour, adjustedMinute)
				});
			}
		});
		for (let i = 1; i <= 8; i++) {
			const totalMinutes = i * 15;
			const hours = Math.floor(totalMinutes / 60);
			const minutes = totalMinutes % 60;
			durations.push(dateUtil.formatTime24Hour(hours, minutes));
		}
		yield put(
			actionCreator.INIT_COMPLETED({
				followUps,
				inPersonStartTimes,
				phoneStartTimes,
				durations,
				opportunityFollowUpStatus: followUpStatusResponse.followUpStatus
			})
		);
	} catch (error: any) {
		yield handleSagaError(error, [actionCreator.INIT_FAILED()]);
	}
}

function* onCreateStart(): any {
	try {
		const type: IFormValue<string> = yield select(selectors.form.getType);
		const date: IFormValue<string> = yield select(selectors.form.getDate);
		const time: IFormValue<string> = yield select(selectors.form.getTime);
		const length: IFormValue<string> = yield select(selectors.form.getLength);
		const specialInstructions: IFormValue<string> = yield select(selectors.form.getSpecialInstructions);
		const opportunityId: string = yield select(selectors.getOpportunityId);
		const rep: any = yield select(authSelectors.getSalesRep);
		const onSuccess: () => PayloadAction<any> = yield select(selectors.getOnSuccess);

		const request: ICreateFollowUpRequest = {
			appointmentDate: date.newValue,
			duration: length.newValue,
			mobileStatus: EMobileStatus.PENDING,
			opportunityId: opportunityId,
			ownerId: rep ? rep.repId : '',
			specialInstructions: specialInstructions.newValue,
			startTime: time.newValue,
			status: EFollowUpStatus.SCHEDULED,
			subType: type.newValue,
			type: 'Follow Up'
		};

		yield api.followUps.createFollowUp(request);
		messagingService.popSnackbar('Follow Up Created', 'success');
		yield put(actionCreator.INIT_FLUSH());
		yield put(actionCreator.RESET_FORM());
		yield put(actionCreator.CREATE_COMPLETED());
		if (onSuccess) {
			yield put(onSuccess());
		}
	} catch (error: any) {
		yield handleSagaError(error, [actionCreator.CREATE_FAILED()]);
	}
}
